import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Cell,
  List,
  Placeholder,
  Section,
  SegmentedControl,
  Select,
  Subheadline,
} from '@telegram-apps/telegram-ui';
import {
  type LeaderboardEntry,
  type LeaderboardGroup,
  type LeaderboardPeriod,
  LeaderboardResponse,
} from '@veylme/shared';

import { apiFetch } from '../api/client.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { useT } from '../hooks/useT.js';
import { hapticSelection } from '../lib/tg.js';
import styles from './LeaderboardScreen.module.css';

const PERIODS: LeaderboardPeriod[] = ['7d', '30d', 'all'];
const GROUPS: LeaderboardGroup[] = ['all', 'ask', 'confession', 'guesswho', 'rate'];

const RANK_MEDALS = ['🥇', '🥈', '🥉'];

/**
 * Экран лидерборда (Веха 9 M3, threat-model rewards.md I.b/R-4/R-7).
 *
 * Публичный: apiFetch добавляет tma-header, сервер игнорирует его на /leaderboard.
 * Cursor-пагинация через nextCursor: «Загрузить ещё» аппендит к списку.
 *
 * Privacy: response НЕ содержит userId — только ownerUsername + counts (R-4).
 * Network-tab check: нет userId ни в query-params, ни в ответе (STOP-gate M3).
 *
 * Веха 13: SegmentedControl для периодов, Cell для записей. Загрузка (первая
 * страница и «ещё») — skeleton-строки (Веха 14.x), пусто/ошибка — Placeholder.
 * Логика не изменена. Экран-вкладка нижней навигации — нативный BackButton не
 * используется (переход через Tabbar).
 */
export function LeaderboardScreen() {
  const t = useT();
  const [period, setPeriod] = useState<LeaderboardPeriod>('7d');
  const [group, setGroup] = useState<LeaderboardGroup>('all');
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  // Ref чтобы отменить устаревший fetch при смене period/group
  const fetchIdRef = useRef(0);

  const loadPage = useCallback(
    async (opts: { period: LeaderboardPeriod; group: LeaderboardGroup; after?: string }) => {
      const id = ++fetchIdRef.current;
      const isFirst = !opts.after;

      if (isFirst) setLoading(true);
      else setLoadingMore(true);
      setError(false);

      try {
        const query: Record<string, string> = {
          period: opts.period,
          group: opts.group,
          limit: '20',
        };
        if (opts.after) query.after = opts.after;

        const data = await apiFetch('/leaderboard', {
          parse: LeaderboardResponse,
          query,
        });

        if (id !== fetchIdRef.current) return; // stale response

        if (isFirst) {
          setEntries(data.entries);
        } else {
          setEntries((prev) => [...prev, ...data.entries]);
        }
        setNextCursor(data.nextCursor);
      } catch {
        if (id !== fetchIdRef.current) return;
        setError(true);
      } finally {
        if (id === fetchIdRef.current) {
          if (isFirst) setLoading(false);
          else setLoadingMore(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    void loadPage({ period, group });
  }, [period, group, loadPage]);

  const handleLoadMore = () => {
    if (nextCursor) void loadPage({ period, group, after: nextCursor });
  };

  const PERIOD_LABELS: Record<LeaderboardPeriod, string> = {
    '7d': t.lb_period_7d,
    '30d': t.lb_period_30d,
    'all': t.lb_period_all,
  };

  const GROUP_LABELS: Record<LeaderboardGroup, string> = {
    'all': t.lb_group_all,
    'ask': t.lb_group_ask,
    'rate': t.lb_group_rate,
    'confession': t.lb_group_confession,
    'guesswho': t.lb_group_guesswho,
    // 'message' — не отдельная вкладка лидерборда (не соревновательный формат,
    // см. GROUPS выше), но лейбл нужен для полноты Record<LeaderboardGroup, ...>.
    'message': t.lb_group_message,
  };

  return (
    <List>
      {/* Вкладки периодов */}
      <Section header={t.lb_title}>
        <div className={styles.segmentedWrap}>
          <SegmentedControl>
            {PERIODS.map((p) => (
              <SegmentedControl.Item
                key={p}
                selected={p === period}
                onClick={() => {
                  hapticSelection();
                  setPeriod(p);
                }}
              >
                {PERIOD_LABELS[p]}
              </SegmentedControl.Item>
            ))}
          </SegmentedControl>
        </div>
      </Section>

      {/* Категория формата — нативный Select (выпадающий список, DESIGN.md §7):
          SegmentedControl не годится для 5 ярлыков разной длины (режутся), а
          выпадающий список вмещает любое число вариантов без обрезки. */}
      <div className={styles.selectWrap}>
        <Select
          header={t.lb_group_label}
          value={group}
          onChange={(e) => {
            hapticSelection();
            setGroup(e.target.value as LeaderboardGroup);
          }}
        >
          {GROUPS.map((g) => (
            <option key={g} value={g}>
              {GROUP_LABELS[g]}
            </option>
          ))}
        </Select>
      </div>

      {/* Состояния загрузки / ошибки / пусто */}
      {loading && <SkeletonRows count={6} />}

      {!loading && error && (
        <Placeholder description={t.lb_error}>
          <Button mode="bezeled" size="m" onClick={() => void loadPage({ period, group })}>
            {t.retry}
          </Button>
        </Placeholder>
      )}

      {!loading && !error && entries.length === 0 && (
        <Placeholder description={t.lb_empty} />
      )}

      {/* Записи лидерборда */}
      {!loading && entries.length > 0 && (
        <Section>
          {entries.map((entry) => (
            <Cell
              key={entry.boxToken}
              before={
                <div
                  className={
                    entry.rank <= 3 ? styles.rankMedal : styles.rankNum
                  }
                >
                  {entry.rank <= 3
                    ? (RANK_MEDALS[entry.rank - 1] ?? entry.rank)
                    : `#${entry.rank}`}
                </div>
              }
              subtitle={`${entry.format} · ${t.lb_questions(entry.questionCount)}${entry.referralCount > 0 ? ` · ${t.lb_referrals(entry.referralCount)}` : ''}`}
              after={
                <Subheadline weight="2" Component="span" className={styles.entryScore}>
                  {entry.score}
                </Subheadline>
              }
            >
              {entry.ownerUsername ? `@${entry.ownerUsername}` : t.lb_anon}
            </Cell>
          ))}
        </Section>
      )}

      {/* Подгрузка следующей страницы — скелетон-строки под списком. */}
      {!loading && loadingMore && <SkeletonRows count={2} />}

      {/* Загрузить ещё */}
      {!loading && !loadingMore && nextCursor && (
        <Section>
          <div className={styles.loadMoreWrap}>
            <Button mode="bezeled" size="m" stretched onClick={handleLoadMore}>
              {t.lb_load_more}
            </Button>
          </div>
        </Section>
      )}
    </List>
  );
}
